import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { getParams } from "@ui/utils/getParams";
import { setParams } from "@ui/utils/setParams";

import { useMessageBus } from "@jti/fe-message-bus";

import type { TranslationsBox } from "../../types";
import { SearchForm } from "../SearchForm";

import styles from "./SearchBox.module.scss";

export type SearchBoxProps = {
  translations: TranslationsBox;
  resultsPage?: string;
  minSearchTextLength?: number;
  navigationFn?: (url: string) => void;
};

export const SearchBox: FC<SearchBoxProps> = ({ translations, resultsPage, minSearchTextLength, navigationFn }) => {
  const messageBus = useMessageBus();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const searchTitle = useMemo(() => {
    if (translations?.searchResultsLabel && searchText) {
      return translations?.searchResultsLabel?.replace("{query}", searchText);
    }
    return translations?.title;
  }, [translations.title, translations?.searchResultsLabel, searchText]);

  const handleSearchSubmit = (searchValue: string) => {
    setSearchText(searchValue);
    setParams({ q: searchValue });
    messageBus.publish("search.value", { payload: { searchValue } });
  };

  useEffect(() => {
    const query = getParams("q")?.q;
    if (query) {
      setSearchText(query);
    }

    messageBus.subscribe({
      channel: "search.total.results",
      callback({ payload: { searchTotalResults, isResultsLoading } }) {
        setTotalResults(searchTotalResults);
        setIsLoading(isResultsLoading);
      },
    });

    return () => {
      messageBus.unsubscribe("search.total.results");
    };
  }, [messageBus]);

  return (
    <div className={styles.container}>
      <div className={styles.searchWrapper} data-testid="searchWrapper">
        {searchTitle && (
          <div className={styles.searchText} data-testid="searchTitle">
            {searchTitle}
          </div>
        )}
        <SearchForm
          translations={translations}
          isInputAutoFocus={false}
          initialSearchValue={searchText ?? undefined}
          resultsPage={resultsPage}
          minSearchTextLength={minSearchTextLength}
          onSearchSubmit={handleSearchSubmit}
          navigationFn={navigationFn}
        />
        {searchText && !isLoading && (
          <div className={styles.totalResults} data-testid="searchTotalResults">
            {translations.resultsLabel?.replace("{count}", String(totalResults ?? 0))}
          </div>
        )}
      </div>
    </div>
  );
};
